import { createPortal } from 'react-dom';
import type { SessionInfo } from '../api/types';
import { api } from '../api/client';
import { prettyModel } from './NewSessionDialog';
import { useInstanceMode } from '../hooks/useInstanceMode';
import './AgentHoverTooltip.css';

// Desktop-only: no hover surface on touch (would fire on tap + has no cursor).
export const HOVER_CAPABLE =
  typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover)').matches;

export interface AgentMetrics {
  turnCount?: number;
  tokens?: number;   // input_tokens_approx — the live context size
  bytes?: number;    // transcript_bytes — the .jsonl history file size
  model?: string;
  effort?: string;
}

// Per-session cache so moving the mouse over the list doesn't re-fetch.
const metricsCache = new Map<string, AgentMetrics>();
export function getCachedMetrics(name: string): AgentMetrics | undefined {
  return metricsCache.get(name);
}
export async function fetchAgentMetrics(name: string): Promise<AgentMetrics> {
  const hit = metricsCache.get(name);
  if (hit) return hit;
  const d = await api.sessions.v2(name, 1);
  const m: AgentMetrics = {
    turnCount: d.turn_count,
    tokens: d.metrics?.input_tokens_approx,
    bytes: d.metrics?.transcript_bytes,
    model: d.metrics?.model,
    effort: d.metrics?.effort,
  };
  metricsCache.set(name, m);
  return m;
}

function fmtBytes(b?: number): string {
  if (b == null || b <= 0) return '—';
  if (b >= 1024 * 1024 * 1024) return `${(b / 1024 / 1024 / 1024).toFixed(1)} GB`;
  if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(b >= 100 * 1024 * 1024 ? 0 : 1)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
}

function fmtTokens(t?: number): string {
  if (t == null || t <= 0) return '—';
  if (t >= 1_000_000) return `${(t / 1_000_000).toFixed(1)}M`;
  if (t >= 1000) return `${(t / 1000).toFixed(t >= 10_000 ? 0 : 1)}k`;
  return String(t);
}
function nodeLabel(n?: string): string {
  return !n || n === 'local' ? 'mac' : n;
}

interface Props {
  session: SessionInfo;
  metrics?: AgentMetrics;
  /** Bounding rect of the hovered trigger — the card anchors to it. */
  rect: DOMRect;
  /** 'right' = beside the trigger (session list); 'below' = under it (header). */
  placement?: 'below' | 'right';
}

/**
 * Hover info card (desktop) — model, node, agent type, status and live size
 * (turns + approx context tokens). Anchored to the trigger via a portal so it
 * escapes any overflow/clipping. Alex 2026-06-23.
 */
export function AgentHoverTooltip({ session, metrics, rect, placement = 'below' }: Props) {
  const instanceMode = useInstanceMode();
  const W = 240;
  const EST_H = 200; // rough card height for viewport clamping
  let left: number;
  let top: number;
  if (placement === 'right') {
    // Beside the row (Alex 2026-06-23: "rechts von der Sessionliste, nicht
    // unterhalb"). Flip to the left of the row if it would run off-screen.
    left = rect.right + 8;
    if (left + W > window.innerWidth - 8) left = Math.max(8, rect.left - W - 8);
    top = Math.min(Math.max(8, rect.top), window.innerHeight - EST_H - 8);
  } else {
    left = Math.min(Math.max(8, rect.left), window.innerWidth - W - 8);
    const below = rect.bottom + 8;
    const wouldOverflow = below + EST_H > window.innerHeight;
    top = wouldOverflow ? Math.max(8, rect.top - 8 - EST_H) : below;
  }

  // Model: pretty label ("Fable 5", "Opus") instead of the raw flag; an EMPTY model
  // means the CLI default was never overridden — say so instead of a bare "—" (#215).
  const rawModel = session.model || metrics?.model || '';
  const rows: Array<[string, string]> = [
    ['Modell', rawModel ? prettyModel(rawModel) : 'Standard (CLI-Default)'],
    ...(instanceMode === 'federation' ? [['Node', nodeLabel(session.node)] as [string, string]] : []),
    ['Agent', session.agent || '—'],
    ['Status', session.status || (session.actually_running === false ? 'offline' : 'running')],
  ];
  // Size is best-effort: turn_count is the live scrape window (not the full
  // history), and input_tokens_approx is currently 0 from the parser. Only
  // show each when it's actually meaningful — a real context-size field is
  // pending a Cloud backend addition (requested 2026-06-23).
  if (metrics?.bytes != null && metrics.bytes > 0) rows.push(['History', fmtBytes(metrics.bytes)]);
  if (metrics?.turnCount != null && metrics.turnCount > 0) rows.push(['Turns', String(metrics.turnCount)]);
  if (metrics?.tokens != null && metrics.tokens > 0) rows.push(['Kontext', `~${fmtTokens(metrics.tokens)} tok`]);
  // Effort: always shown (#215) — '—' when the registry has none, not a missing row.
  rows.push(['Effort', session.effort || '—']);
  if (session.owner_email || session.owner) rows.push(['Owner', session.owner_email || session.owner!]);

  return createPortal(
    <div className="aht" style={{ top, left, width: W }} role="tooltip">
      <div className="aht-name">{session.display_name || session.name}</div>
      <dl className="aht-rows">
        {rows.map(([k, v]) => (
          <div className="aht-row" key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
    </div>,
    document.body,
  );
}
